import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Sparkles } from 'lucide-react';
import Image from 'next/image';
import { useImageGeneration, GenerationTab } from '.';
import {
  useGenerateForUseCase,
  useFreeForm,
} from '@/lib/hooks/queries/usePrompts';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { usePromptStore } from '@/lib/store/promptStore';

export default function GenerateButton() {
  const queryClient = useQueryClient();

  const { model, generationTab, useCaseType, gender, prompt } =
    useImageGeneration();

  const { mutateAsync: generateForUseCase, isPending: isGeneratingUseCase } =
    useGenerateForUseCase();
  const { mutateAsync: generateFreeForm, isPending: isGeneratingFreeForm } =
    useFreeForm();
  const { setLatestPrompt } = usePromptStore();

  // Check if all required options are selected for use case tab
  const isUseCaseTabValid = () => {
    if (generationTab !== GenerationTab.USE_CASE) return true;

    return !!(
      (
        model && // Model is selected
        useCaseType && // Use case is selected
        gender
      ) // Gender is selected
    );
  };

  // Check if all required options are selected for free form tab
  const isFreeFormTabValid = () => {
    if (generationTab !== GenerationTab.FREE_FORM) return true;

    return !!(
      (
        model && // Model is selected
        prompt.trim() && // Prompt is not empty
        gender
      ) // Gender is selected
    );
  };

  // Get missing requirements for better UX
  const getMissingRequirements = () => {
    if (generationTab === GenerationTab.USE_CASE) {
      const missing = [];
      if (!model) missing.push('Model');
      if (!useCaseType) missing.push('Use Case');
      if (!gender) missing.push('Gender');
      return missing;
    } else if (generationTab === GenerationTab.FREE_FORM) {
      const missing = [];
      if (!model) missing.push('Model');
      if (!prompt.trim()) missing.push('Prompt');
      if (!gender) missing.push('Gender');
      return missing;
    }
    return [];
  };

  const handleGenerate = async () => {
    if (
      generationTab === GenerationTab.USE_CASE &&
      model &&
      useCaseType &&
      gender
    ) {
      // Additional check for required model data
      if (!model.triggerWord || !model.modelUrl) {
        toast.error(
          'Selected model is missing required data. Please select a different model.',
        );
        return;
      }

      try {
        await generateForUseCase(
          {
            useCase: useCaseType,
            triggerWord: model.triggerWord,
            gender: gender,
            modelUrl: model.modelUrl,
            modelId: model.modelId,
          },
          {
            onSuccess: (data) => {
              setLatestPrompt({
                prompt: useCaseType,
                model_id: model.modelId,
                is_use_case: true,
                output_images_urls: data.output,
              });

              toast.success(
                'Image generation started! Check your history for results.',
              );
              queryClient.invalidateQueries({
                queryKey: ['prompts', 'recent'],
              });
            },
          },
        );
      } catch (error) {
        toast.error('Failed to generate image. Please try again.');
        console.error('Error generating image:', error);
      }
    } else if (generationTab === GenerationTab.FREE_FORM) {
      // Handle free form generation
      if (!prompt.trim()) {
        toast.error('Please enter a prompt for free form generation.');
        return;
      }

      if (!model?.modelUrl) {
        toast.error('Please select a model for free form generation.');
        return;
      }

      if (!gender) {
        toast.error('Please select a gender for free form generation.');
        return;
      }

      try {
        await generateFreeForm(
          {
            triggerWord: model.triggerWord || '',
            gender: gender,
            modelUrl: model.modelUrl,
            modelId: model.modelId,
            freeText: prompt.trim(),
          },
          {
            onSuccess: (data) => {
              setLatestPrompt({
                prompt: prompt.trim(),
                model_id: model.modelId,
                is_use_case: false,
                output_images_urls: data.output,
              });

              toast.success(
                'Image generation started! Check your history for results.',
              );
              queryClient.invalidateQueries({
                queryKey: ['prompts', 'recent'],
              });
            },
          },
        );
      } catch (error) {
        toast.error('Failed to generate image. Please try again.');
        console.error('Error generating image:', error);
      }
    }
  };

  const isGenerating = isGeneratingUseCase || isGeneratingFreeForm;
  const isButtonDisabled = !isUseCaseTabValid() || !isFreeFormTabValid();
  const missingRequirements = getMissingRequirements();

  return (
    <div className='px-4 pb-6'>
      {/* Required fields status */}
      <div className='mb-4 p-3 bg-neutral-900/50 rounded-lg border border-neutral-700/50'>
        <Label className='text-sm font-medium text-neutral-300 mb-3 block'>
          Required Fields Status:
        </Label>
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <span className='text-xs text-neutral-400'>Model</span>
            <div className='flex items-center gap-2'>
              {model ? (
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              ) : (
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
              )}
              <span
                className={`text-xs ${
                  model ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {model ? 'Selected' : 'Required'}
              </span>
            </div>
          </div>

          {generationTab === GenerationTab.USE_CASE && (
            <>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-neutral-400'>Use Case</span>
                <div className='flex items-center gap-2'>
                  {useCaseType ? (
                    <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  ) : (
                    <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                  )}
                  <span
                    className={`text-xs ${
                      useCaseType ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {useCaseType ? 'Selected' : 'Required'}
                  </span>
                </div>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-xs text-neutral-400'>Gender</span>
                <div className='flex items-center gap-2'>
                  {gender ? (
                    <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  ) : (
                    <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                  )}
                  <span
                    className={`text-xs ${
                      gender ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {gender ? 'Selected' : 'Required'}
                  </span>
                </div>
              </div>
            </>
          )}

          {generationTab === GenerationTab.FREE_FORM && (
            <>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-neutral-400'>Prompt</span>
                <div className='flex items-center gap-2'>
                  {prompt.trim() ? (
                    <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  ) : (
                    <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                  )}
                  <span
                    className={`text-xs ${
                      prompt.trim() ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {prompt.trim() ? 'Entered' : 'Required'}
                  </span>
                </div>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-xs text-neutral-400'>Gender</span>
                <div className='flex items-center gap-2'>
                  {gender ? (
                    <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  ) : (
                    <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                  )}
                  <span
                    className={`text-xs ${
                      gender ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {gender ? 'Selected' : 'Required'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className='flex items-center gap-2 mb-2'>
        <Image src='/coin.svg' alt='Coin' width={12} height={12} />
        <Label className='text-stone-50/50 font-inter text-sm'>
          450 credits
        </Label>
      </div>

      {/* Show missing requirements */}
      {isButtonDisabled && missingRequirements.length > 0 && (
        <div className='mb-3 p-2 bg-yellow-900/20 border border-yellow-600/30 rounded-md'>
          <Label className='text-yellow-400 text-xs font-medium'>
            Missing: {missingRequirements.join(', ')}
          </Label>
        </div>
      )}

      <Button
        size='lg'
        disabled={isButtonDisabled || isGenerating}
        onMouseDown={handleGenerate}
        className='w-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-stone-50/90 font-semibold text-base rounded-lg transition-all flex items-center justify-center gap-2'
      >
        {isGenerating ? (
          <>
            <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className='w-5 h-5' />
            Generate
          </>
        )}
      </Button>
    </div>
  );
}
